// VerifySignaturesDialog.tsx (упрощенная версия с использованием хука)

import { Classes, Dialog } from "@blueprintjs/core"
import axios from 'axios'
import { observer } from "mobx-react"
import { Buttons } from "modules/common/components/form"
import { LocalLoader } from "modules/common/components/loader/Loader"
import { PlanrButton } from 'modules/common/components/planr/button/Button'
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { apiUrls } from 'modules/common/services/communication/urls'
import { PendingSignatureItemType, SignatureDtoType } from "modules/main/models/order-content-task-signatures"
import { Constants } from 'modules/root/models/constants'
import React, { useEffect, useState } from 'react'
import { EmployeeSignatureFileType } from '../signatures-list/models/employee-signatures'
import { describeCryptoError, SignatureInfo, useCryptoPro } from './useCryptoPro'
import styles from "./VerifySignaturesDialog.module.scss"

interface VerifySignaturesDialogProps {
  isOpen: boolean
  signature: PendingSignatureItemType | EmployeeSignatureFileType | null
  onClose: () => void
  baseUrl: string
  onGetSignature: () => Promise<SignatureDtoType>
  onNotifyError?: (error: string) => void
  onNotifySuccess?: (message: string) => void
}

export const VerifySignaturesDialog: React.FC<VerifySignaturesDialogProps> = observer(({
  isOpen,
  signature,
  onClose,
  baseUrl,
  onGetSignature,
  onNotifyError,
  onNotifySuccess
}) => {
  const { verifySignature, fetchCerts, loading: cryptoLoading, error: cryptoError } = useCryptoPro()

  const [loading, setLoading] = useState(false)
  const [signatures, setSignatures] = useState<SignatureInfo[]>([])
  const [verificationStatus, setVerificationStatus] = useState<'success' | 'error' | null>(null)
  const [verificationMessage, setVerificationMessage] = useState<string>('')
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    if (isOpen && !isInitialized) {
      // Вызываем fetchCerts для инициализации плагина и получения доступа
      fetchCerts().then(() => {
        setIsInitialized(true)
      }).catch((error) => {
        if (onNotifyError) {
          onNotifyError(`Ошибка инициализации КриптоПро: ${error.message}`)
        }
      })
    }
  }, [isOpen, fetchCerts, isInitialized, onNotifyError])

  // Загружаем файл и подпись после инициализации плагина
  useEffect(() => {
    if (isOpen && signature && isInitialized && !cryptoLoading && !cryptoError) {
      loadAndVerifySignatures()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, signature, isInitialized, cryptoLoading, cryptoError])


  const loadAndVerifySignatures = async () => {
    if (!signature) return

    setLoading(true)
    setSignatures([])
    setVerificationStatus(null)
    setVerificationMessage('')

    try {
      // 1. Получаем подпись из базы
      const signatureDto = await onGetSignature()

      if (!signatureDto.signature) {
        throw new Error('Подпись в базе данных отсутствует')
      }
      // 2. Скачиваем файл
      const fileUrl = baseUrl + apiUrls.application.files.content(signature.fileId, signature.fileName)
      const fileResponse = await axios.get(fileUrl, {
        responseType: 'arraybuffer',
        withCredentials: true
      })

      // 3. Используем метод verifySignature из хука
      const result = await verifySignature(signatureDto.signature, fileResponse.data)

      setSignatures(result.signatures)

      if (result.verified) {
        setVerificationStatus('success')
        setVerificationMessage(`Подписи соответствуют файлу. Найдено подписей: ${result.signatures.length}`)

        if (onNotifySuccess) {
          onNotifySuccess(`Проверено подписей: ${result.signatures.length}`)
        }
      } else {
        const reason = result.error || 'подпись не соответствует файлу'

        setVerificationStatus('error')
        setVerificationMessage(result.signatures.length > 0
          ? `Проверка не пройдена: ${reason}. Ниже — данные подписантов из файла подписи.`
          : `Проверка не пройдена: ${reason}`)

        if (onNotifyError) {
          onNotifyError(`Не удалось проверить подписи: ${reason}`)
        }
      }

    } catch (error) {
      const reason = describeCryptoError(error)

      setVerificationStatus('error')
      setVerificationMessage(`Ошибка: ${reason}`)
      if (onNotifyError) {
        onNotifyError(`Не удалось проверить подписи: ${reason}`)
      }
    } finally {
      setLoading(false)
    }
  }
  const getRoleLabel = (roleId: string): string => {
    if (!roleId) return ''

    const role = Constants.signatureTypeList.find((item) => item.id === roleId)
    return role ? role.label : roleId // Если не нашли, возвращаем исходный id
  }

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatDateTime = (dateTime?: string): string => {
    if (!dateTime) return '—'
    return new Date(dateTime).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const truncateString = (str: string, maxLength: number = 50): string => {
    if (!str) return '—'
    if (str.length <= maxLength) return str
    return str.substring(0, maxLength) + '…'
  }

  return (
    <Dialog
      title="Просмотр подписей документа"
      isOpen={isOpen}
      onClose={onClose}
      usePortal={true}
      portalClassName="verify-signatures-dialog-portal" // <-- Добавляем portalClassName
      backdropClassName="standard"
      className={`${styles.verifyDialog} ${styles.smallDialog}`}
      style={{ zIndex: 10000 }}
    >
      <div className={Classes.DIALOG_BODY}>
        <div className={styles.verifyContainer}>

          {/* Статус криптоплагина */}
          {cryptoLoading && (
            <div className={`${styles.cryptoStatus} ${styles.info}`}>
              <LocalLoader active={true} />
              <span>Загрузка криптоплагина...</span>
            </div>
          )}

          {cryptoError && (
            <div className={`${styles.cryptoStatus} ${styles.error}`}>
              <GeneralIcon type="general-warning" style={{ marginRight: '8px' }} />
              <span>Ошибка КриптоПро: {cryptoError}</span>
            </div>
          )}

          {/* Информация о документе
          {signature && (
            <div className={styles.docInfo}>
              <div className={styles.signRow}>
                <span className={styles.label}>Файл:</span>
                <span className={styles.value}>
                  <FileLink
                    baseUrl={baseUrl}
                    file={{
                      fileId: signature.fileId,
                      fileName: signature.fileName
                    }}
                    onRemove={eat}
                    readOnly
                    style={{
                      whiteSpace: "normal",
                      fontSize: "10px",
                      height: "auto",
                      padding: "8px 12px",
                      borderColor: "#ea561e",
                      color: "#ea561e"
                    }}
                  />
                  {signature.version && (
                    <span className={styles.version}> (версия {signature.version})</span>
                  )}
                </span>
              </div>
            </div>
          )} */}

          {/* Статус загрузки */}
          {loading && (
            <div className={styles.loadingState}>
              <LocalLoader active={true} />
              <span>Загрузка информации о подписях...</span>
            </div>
          )}

          {/* Статус верификации */}
          {verificationStatus && !loading && !cryptoLoading && (
            <div className={`${styles.verificationStatus} ${styles[verificationStatus]}`}>
              <GeneralIcon
                type={verificationStatus === 'success' ? 'general-check' : 'general-warning'}
                style={{ marginRight: '8px' }}
              />
              <span>{verificationMessage}</span>
            </div>
          )}

          {/* Список подписей */}
          {!loading && signatures.length > 0 && (
            <div className={styles.signaturesList}>

              {signatures.map((sig, idx) => (
                <div key={idx} className={styles.signatureCard}>
                  <div className={styles.signatureHeader}>
                    <div className={styles.signatureNumber}>
                      <GeneralIcon type="general-signature" />
                      <span>Подпись #{sig.number}</span>
                    </div>
                    {sig.signingTime && (
                      <div className={styles.signingTime}>
                        <GeneralIcon type="general-time" />
                        <span>{formatDateTime(sig.signingTime)}</span>
                      </div>
                    )}
                  </div>

                  <div className={styles.signatureContent}>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>Владелец:</span>
                      <span className={styles.infoValue} title={sig.subjectName}>
                        {truncateString(sig.subjectName, 70)}
                      </span>
                    </div>

                    {sig.signerRole && (
                      <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Тип подписи:</span>
                        <span className={`${styles.infoValue} ${styles.role}`}>
                          {getRoleLabel(sig.signerRole)}
                        </span>
                      </div>
                    )}

                    {sig.comment && (
                      <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Комментарий:</span>
                        <span className={styles.infoValue}>{sig.comment}</span>
                      </div>
                    )}

                    {sig.issuerName && (
                      <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Издатель:</span>
                        <span className={styles.infoValue} title={sig.issuerName}>
                          {truncateString(sig.issuerName, 70)}
                        </span>
                      </div>
                    )}

                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>Серийный номер:</span>
                      <span className={styles.infoValue}>{sig.serialNumber}</span>
                    </div>

                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>Действителен до:</span>
                      <span className={`${styles.infoValue} ${sig.validTo < new Date() ? styles.expired : ''}`}>
                        {formatDate(sig.validTo)}
                        {sig.validTo < new Date() && ' (просрочен)'}
                      </span>
                    </div>

                    {sig.thumbprint && (
                      <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Отпечаток:</span>
                        <span className={styles.infoValueThumbprint} title={sig.thumbprint}>
                          {truncateString(sig.thumbprint, 35)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

          )}

          {/* Нет подписей */}
          {!loading && signatures.length === 0 && verificationStatus !== 'error' && !cryptoLoading && (
            <div className={styles.noSignatures}>
              <GeneralIcon type="general-info" style={{ fontSize: '40px', color: '#c0cbd4' }} />
              <p>Подписи не найдены</p>
              <span>Документ не содержит электронных подписей</span>
            </div>
          )}
        </div>
      </div>

      <div className={Classes.DIALOG_FOOTER}>
        <Buttons
          left={
            <div className={styles.dialogButtons}>
              <PlanrButton size="small" type="graybtn" onClick={onClose}>
                Закрыть
              </PlanrButton>
            </div>
          }
        />
      </div>
    </Dialog>
  )
})

export default VerifySignaturesDialog